<template>
  <div class="section-modals">
    <!-- Modal de confirmation de suppression -->
    <Teleport to="body">
      <div v-if="sectionsStore.showDeleteModal" class="section-modal-overlay" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="section-modal-container">
          <div class="section-modal-content section-modal-content-sm">
            <div class="section-modal-header">
              <h3 class="section-modal-title">Supprimer la section ?</h3>
              <div class="section-modal-body">
                <p class="text-sm text-gray-600 whitespace-pre-line">{{ deleteModalMessage }}</p>
              </div>
            </div>
            
            <div class="section-modal-footer">
              <button
                class="section-modal-btn section-modal-btn-danger"
                :disabled="!canDelete"
                @click="confirmDeleteSection"
              >
                Supprimer
              </button>
              <button
                class="section-modal-btn section-modal-btn-cancel"
                @click="closeDeleteModal"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal de traduction -->
    <Teleport to="body">
      <div v-if="sectionsStore.showTranslationModal" class="section-modal-overlay" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="section-modal-container">
          <div class="section-modal-content section-modal-content-sm">
            <div class="section-modal-header">
              <h3 class="section-modal-title">
                {{ editingTranslation ? 'Modifier la traduction' : 'Ajouter une traduction' }}
              </h3>
              
              <div class="section-modal-body">
                <!-- Sélection de la langue -->
                <div class="section-form-group">
                  <label class="section-form-label">Langue</label>
                  <select 
                    v-model="newTranslation.language_code"
                    class="section-form-select"
                    :disabled="!!editingTranslation"
                  >
                    <option 
                      v-for="lang in availableLanguagesForSection"
                      :key="lang.code" 
                      :value="lang.code"
                    >
                      {{ lang.name }} ({{ lang.code }})
                    </option>
                  </select>
                </div>
                
                <!-- Champ titre -->
                <div class="section-form-group">
                  <label class="section-form-label">Titre</label>
                  <input 
                    v-model="newTranslation.titre"
                    type="text"
                    class="section-form-input"
                    placeholder="Entrez le titre"
                  />
                </div>
                
                <!-- Champ description -->
                <div class="section-form-group">
                  <label class="section-form-label">Description</label>
                  <textarea 
                    v-model="newTranslation.description"
                    class="section-form-textarea"
                    rows="3"
                    placeholder="Entrez la description"
                  ></textarea>
                </div>
              </div>
            </div>
            
            <div class="section-modal-footer">
              <button
                v-if="editingTranslation"
                class="section-modal-btn section-modal-btn-danger"
                @click="deleteTranslation"
              >
                <TrashIcon class="w-5 h-5" />
              </button>
              <button
                class="section-modal-btn section-modal-btn-primary"
                @click="saveTranslation"
              >
                <CheckIcon class="w-5 h-5" />
              </button>
              <button
                class="section-modal-btn section-modal-btn-cancel"
                @click="closeTranslationModal"
              >
                <XIcon class="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
    
    <!-- Modal de condition -->
    <Teleport to="body">
      <div v-if="sectionsStore.showConditionModal" class="section-modal-overlay" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="section-modal-container">
          <div class="section-modal-content section-modal-content-sm">
            <div class="section-modal-header">
              <h3 class="section-modal-title">
                {{ editingConfig ? 'Modifier la condition' : 'Ajouter une condition' }}
              </h3>
              
              <div class="section-modal-body">
                <!-- Sélection du champ conditionneur -->
                <div class="section-form-group">
                  <label class="section-form-label">Champ conditionneur</label>
                  <select 
                    v-model="newConfig.condition_id"
                    class="section-form-select"
                    @change="onConditionerChange"
                  >
                    <option value="">Sélectionnez un champ</option>
                    <option 
                      v-for="section in availableConditioners"
                      :key="section.id"
                      :value="section.id"
                    >
                      {{ formatConditionerName(section.name) }} ({{ section.type }})
                    </option>
                  </select>
                  
                  <!-- Aperçu du champ sélectionné -->
                  <div v-if="newConfig.condition_id" class="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div class="text-sm text-blue-800">
                      <strong>Champ sélectionné :</strong>
                      {{ formatConditionerName(availableConditioners.find(s => s.id === newConfig.condition_id)?.name || '') }}
                    </div>
                    <div class="text-xs text-blue-600 mt-1">
                      Type : {{ availableConditioners.find(s => s.id === newConfig.condition_id)?.type }}
                    </div>
                  </div>
                </div>
                
                <!-- Sélection de la condition -->
                <div v-if="newConfig.condition_id" class="section-form-group">
                  <label class="section-form-label">Condition</label>
                  <select 
                    v-model="newConfig.condition_value"
                    class="section-form-select"
                  >
                    <option 
                      v-for="condition in availableConditionsForField"
                      :key="condition.value"
                      :value="condition.value"
                    >
                      {{ condition.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            
            <div class="section-modal-footer">
              <button
                class="section-modal-btn section-modal-btn-primary"
                @click="saveCondition"
              >
                <CheckIcon class="w-5 h-5" />
              </button>
              <button
                class="section-modal-btn section-modal-btn-cancel"
                @click="closeConditionModal"
              >
                <XIcon class="w-5 h-5" />
              </button>
              <button
                v-if="editingConfig"
                class="section-modal-btn section-modal-btn-danger"
                @click="deleteCondition(editingConfig)"
              >
                <TrashIcon class="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
    
    <!-- Modal de création de section racine -->
    <Teleport to="body">
      <div v-if="sectionsStore.showAddRootModal" class="section-modal-overlay" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="section-modal-container">
          <div class="section-modal-content section-modal-content-sm">
            <div class="section-modal-header">
              <h3 class="section-modal-title">Ajouter une section racine</h3>
              
              <div class="section-modal-body">
                <!-- Champ nom -->
                <div class="section-form-group">
                  <label class="section-form-label">Nom</label>
                  <input 
                    v-model="newRootSection.name"
                    type="text"
                    class="section-form-input"
                    placeholder="Nom de la section racine"
                  />
                </div>
                
                <!-- Champ description -->
                <div class="section-form-group">
                  <label class="section-form-label">Description</label>
                  <textarea 
                    v-model="newRootSection.description"
                    class="section-form-textarea"
                    rows="3"
                    placeholder="Description de la section"
                  ></textarea>
                </div>
                
                <!-- Options -->
                <div class="section-form-group">
                  <div class="space-y-2">
                    <label class="section-form-checkbox-label">
                      <input 
                        v-model="newRootSection.display"
                        type="checkbox"
                        class="section-form-checkbox"
                      />
                      Afficher cette section
                    </label>
                    <label class="section-form-checkbox-label">
                      <input 
                        v-model="newRootSection.mandatory_entry"
                        type="checkbox"
                        class="section-form-checkbox"
                      />
                      Section obligatoire
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="section-modal-footer">
              <button
                class="section-modal-btn section-modal-btn-primary"
                @click="createRootSection"
              >
                <CheckIcon class="w-5 h-5" />
              </button>
              <button
                class="section-modal-btn section-modal-btn-cancel"
                @click="closeAddRootModal"
              >
                <XIcon class="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { useSectionsStore } from '@/stores/sectionsStore';
import { useAdminPermissions } from '@/composables/useAdminPermissions';
import type { SectionExt } from '@/stores/sectionsStore';
import type { Translation, TranslationCreate } from '@/api/types/core/translations';
import type { FieldConfigResponse, FieldConfigCreate } from '@/api/field_config';
import * as translationsApi from '@/api/translations';
import { createConfig, updateConfig, deleteConfig } from '@/api/field_config';
import { SectionUtils } from '@/utils/sectionUtils';
import { CheckIcon, XIcon, TrashIcon } from 'lucide-vue-next';

export interface LanguageOption {
  code: string;
  name: string;
}

export default defineComponent({
  name: 'SectionModals',
  components: {
    CheckIcon,
    XIcon,
    TrashIcon,
  },
  emits: ['show-notification'],
  setup(_, { emit }) {
    const sectionsStore = useSectionsStore();
    const { canCreate, canEdit, canDelete } = useAdminPermissions();
    

    
    // État local pour les modaux
    const sectionToDelete = ref<SectionExt | null>(null);
    const currentSection = ref<SectionExt | null>(null);
    const editingTranslation = ref<Translation | null>(null);
    const editingConfig = ref<FieldConfigResponse | null>(null);
    
    // État des formulaires
    const newTranslation = ref<TranslationCreate>({
      reference_id: 0,
      language_code: 'fr',
      titre: '',
      description: '',
    });
    
    const newConfig = ref({
      reference_id: null as number | null,
      condition_id: null as number | null,
      condition_value: 'true',
    });
    
    const newRootSection = ref({
      name: '',
      description: '',
      display: true,
      mandatory_entry: false,
    });
    
    // Langues disponibles
    const availableLanguages: LanguageOption[] = [
      { code: 'fr', name: 'Français' },
      { code: 'en', name: 'English' },
      { code: 'de', name: 'Deutsch' },
    ];
    
    // Conditions disponibles par type de champ
    const availableConditions = {
      bool: [
        { value: 'true', label: 'Est vrai' },
        { value: 'false', label: 'Est faux' },
      ],
      text: [
        { value: 'not_null', label: 'Est rempli' },
      ],
      textarea: [
        { value: 'not_null', label: 'Est rempli' },
      ],
    };

    // Computed properties
    const deleteModalMessage = computed(() => {
      if (!sectionToDelete.value) return '';
      
      const hasSubsections = sectionsStore.sections.some(s => s.name.startsWith(sectionToDelete.value!.name + '.'));
      const childrenCount = sectionsStore.getAllDescendants(sectionToDelete.value.id).length;
      
      // Vérifier les conditions qui dépendent de cette section
      const dependentConditions = sectionsStore.configs.filter(config => 
        config.condition_id === sectionToDelete.value!.id
      );
      
      // Vérifier les schémas qui utilisent cette section (simulation - à adapter selon votre API)
      const impactedSchemas: Array<{name: string, type: string}> = [
        // Simulation de schémas impactés - à remplacer par un vrai appel API
        { name: "Acquisition - Lasergrammétrie", type: "Acquisition" },
        { name: "Documentation - Photographie", type: "Documentation" },
        { name: "Analyse - Spectrométrie", type: "Analyse" },
        { name: "Conservation - Traitement", type: "Conservation" },
        { name: "Restauration - Nettoyage", type: "Restauration" },
        { name: "Étude - Datation", type: "Étude" },
      ].slice(0, Math.floor(Math.random() * 7)); // Simulation aléatoire
      
      let message = `⚠️ Suppression de "${sectionToDelete.value.name}"\n\n`;
      
      if (hasSubsections) {
        message += `📁 ${childrenCount} sous-section(s) seront également supprimées\n\n`;
      }
      
      if (dependentConditions.length > 0) {
        message += `🔗 ${dependentConditions.length} condition(s) dépendante(s) seront supprimées :\n`;
        dependentConditions.forEach(config => {
          const dependentSection = sectionsStore.sections.find(s => s.id === config.reference_id);
          if (dependentSection) {
            message += `  • ${dependentSection.name} (condition: ${config.condition_value})\n`;
          }
        });
        message += '\n';
      }
      
      if (impactedSchemas.length > 0) {
        message += `📋 Schémas impactés :\n`;
        const schemasToShow = impactedSchemas.slice(0, 5);
        schemasToShow.forEach(schema => {
          message += `  • ${schema.name}\n`;
        });
        if (impactedSchemas.length > 5) {
          message += `  ... et ${impactedSchemas.length - 5} autre(s) schéma(s)\n`;
        }
        message += '\n';
      }
      
      message += '❌ Cette action ne peut pas être annulée.';
      
      return message;
    });

    const availableLanguagesForSection = computed(() => {
      if (!currentSection.value || editingTranslation.value) {
        return availableLanguages;
      }
      const existingLanguages = new Set(
        currentSection.value.translations?.map(t => t.language_code) || []
      );
      return availableLanguages.filter(lang => !existingLanguages.has(lang.code));
    });

    const availableConditioners = computed(() => {
      return sectionsStore.sections.filter(s => isFinalSection(s) && s.type);
    });

    const availableConditionsForField = computed(() => {
      if (!newConfig.value.condition_id) return [];
      
      const selectedSection = sectionsStore.sections.find(s => s.id === newConfig.value.condition_id);
      if (!selectedSection) return [];
      
      return availableConditions[selectedSection.type as keyof typeof availableConditions] || [];
    });

    // Méthodes utilitaires
    const isFinalSection = (section: SectionExt): boolean => {
      const sectionFullName = section.name;
      return !sectionsStore.sections.some(s => s.name.startsWith(sectionFullName + '.'));
    };

    const getNextRootOrder = (): number => {
      const rootSections = sectionsStore.sections.filter(s => !s.name.includes('.'));
      
      if (rootSections.length === 0) return 1;
      
      const maxOrder = Math.max(...rootSections.map(s => s.ordre ?? 0));
      return maxOrder + 1;
    };

    const showNotification = (message: string, type: string): void => {
      emit('show-notification', message, type);
    };

    const formatConditionerName = (fullName: string): string => {
      // Transformer "HOW.soussection.field" en "HOW → soussection → field"
      return fullName.replace(/\./g, ' → ');
    };

    // Actions pour le modal de suppression
    const openDeleteModal = (section: SectionExt): void => {
      sectionToDelete.value = section;
      sectionsStore.showDeleteModal = true;
    };

    const closeDeleteModal = (): void => {
      sectionsStore.showDeleteModal = false;
      sectionToDelete.value = null;
    };

    const confirmDeleteSection = async (): Promise<void> => {
      if (!sectionToDelete.value) return;
      
      try {
        const result = await sectionsStore.deleteSection(sectionToDelete.value.id);
        if (result.success) {
          showNotification("Section et sous-sections supprimées avec succès.", "success");
        } else {
          showNotification("Erreur lors de la suppression de la section.", "error");
        }
      } finally {
        closeDeleteModal();
      }
    };

    // Actions pour le modal de traduction
    const openTranslationModal = (sectionOrTranslation: SectionExt | Translation, translation?: Translation): void => {
      // Si le premier paramètre est une traduction (cas edit-translation)
      if ('language_code' in sectionOrTranslation) {
        const translationObj = sectionOrTranslation as Translation;
        const section = sectionsStore.sections.find(s => s.id === translationObj.reference_id);
        if (!section) {
          console.error('Section non trouvée pour la traduction:', translationObj);
          showNotification("Erreur: Section non trouvée pour cette traduction.", "error");
          return;
        }
        currentSection.value = section;
        editingTranslation.value = translationObj;
        newTranslation.value = {
          reference_id: translationObj.reference_id,
          language_code: translationObj.language_code,
          titre: translationObj.titre,
          description: translationObj.description,
        };
      } else {
        // Cas normal avec section
        const section = sectionOrTranslation as SectionExt;
        currentSection.value = section;
        
        if (translation) {
          editingTranslation.value = translation;
          newTranslation.value = {
            reference_id: translation.reference_id,
            language_code: translation.language_code,
            titre: translation.titre,
            description: translation.description,
          };
        } else {
          editingTranslation.value = null;
          newTranslation.value = {
            reference_id: section.id,
            language_code: availableLanguagesForSection.value[0]?.code || 'fr',
            titre: '',
            description: '',
          };
        }
      }
      
      sectionsStore.showTranslationModal = true;
    };

    const closeTranslationModal = (): void => {
      sectionsStore.showTranslationModal = false;
      currentSection.value = null;
      editingTranslation.value = null;
      newTranslation.value = {
        reference_id: 0,
        language_code: 'fr',
        titre: '',
        description: '',
      };
    };

    const saveTranslation = async (): Promise<void> => {
      if (!currentSection.value) return;
      
      try {
        if (editingTranslation.value) {
          // Mise à jour d'une traduction existante
          await translationsApi.updateTranslation(editingTranslation.value.id, {
            reference_id: currentSection.value.id,
            language_code: newTranslation.value.language_code,
            titre: newTranslation.value.titre,
            description: newTranslation.value.description,
          });
          showNotification("Traduction mise à jour avec succès.", "success");
        } else {
          // Vérification des doublons
          const existingTranslation = currentSection.value.translations?.find(
            t => t.language_code === newTranslation.value.language_code
          );
          if (existingTranslation) {
            showNotification("Une traduction existe déjà pour cette langue.", "error");
            return;
          }
          
          // Création d'une nouvelle traduction
          await translationsApi.createTranslation({
            reference_id: currentSection.value.id,
            language_code: newTranslation.value.language_code,
            titre: newTranslation.value.titre,
            description: newTranslation.value.description,
          });
          showNotification("Traduction ajoutée avec succès.", "success");
        }
        
        // Mise à jour locale optimisée des traductions
        if (editingTranslation.value) {
          // Mettre à jour la traduction existante
          const sectionIndex = sectionsStore.sections.findIndex(s => s.id === currentSection.value!.id);
          if (sectionIndex !== -1) {
            const translationIndex = sectionsStore.sections[sectionIndex].translations?.findIndex(t => t.id === editingTranslation.value!.id);
            if (translationIndex !== undefined && translationIndex !== -1 && sectionsStore.sections[sectionIndex].translations) {
              sectionsStore.sections[sectionIndex].translations[translationIndex] = {
                ...sectionsStore.sections[sectionIndex].translations[translationIndex],
                titre: newTranslation.value.titre,
                description: newTranslation.value.description,
              };
            }
          }
        } else {
          // Ajouter la nouvelle traduction localement
          const sectionIndex = sectionsStore.sections.findIndex(s => s.id === currentSection.value!.id);
          if (sectionIndex !== -1) {
            if (!sectionsStore.sections[sectionIndex].translations) {
              sectionsStore.sections[sectionIndex].translations = [];
            }
            // Note: created_at et updated_at seront définis par le backend lors du prochain fetch
            const tempTranslation: Translation = {
              id: Date.now(), // ID temporaire
              reference_id: currentSection.value!.id,
              language_code: newTranslation.value.language_code,
              titre: newTranslation.value.titre,
              description: newTranslation.value.description,
              created_at: '', // Sera défini par le backend
              updated_at: '', // Sera défini par le backend
            };
            sectionsStore.sections[sectionIndex].translations!.push(tempTranslation);
          }
        }
        
        closeTranslationModal();
      } catch (error) {
        showNotification(
          editingTranslation.value 
            ? "Erreur lors de la mise à jour de la traduction." 
            : "Erreur lors de l'ajout de la traduction.",
          "error"
        );
      }
    };

    const deleteTranslation = async (): Promise<void> => {
      if (!editingTranslation.value) return;
      
      try {
        await translationsApi.deleteTranslation(editingTranslation.value.id);
        showNotification("Traduction supprimée avec succès.", "success");
        
        // Mise à jour locale optimisée - supprimer la traduction de la liste
        if (currentSection.value) {
          const sectionIndex = sectionsStore.sections.findIndex(s => s.id === currentSection.value!.id);
          if (sectionIndex !== -1 && sectionsStore.sections[sectionIndex].translations) {
            sectionsStore.sections[sectionIndex].translations = sectionsStore.sections[sectionIndex].translations!.filter(
              t => t.id !== editingTranslation.value!.id
            );
          }
        }
        
        closeTranslationModal();
      } catch (error) {
        showNotification("Erreur lors de la suppression de la traduction.", "error");
      }
    };

    // Actions pour le modal de condition
    const openConditionModal = (section: SectionExt, config?: FieldConfigResponse): void => {
      if (config) {
        editingConfig.value = config;
        newConfig.value = {
          reference_id: config.reference_id,
          condition_id: config.condition_id || null,
          condition_value: config.condition_value || '',
        };
        onConditionerChange();
      } else {
        editingConfig.value = null;
        newConfig.value = {
          reference_id: section.id,
          condition_id: null,
          condition_value: '',
        };
      }
      sectionsStore.showConditionModal = true;
    };

    const closeConditionModal = (): void => {
      sectionsStore.showConditionModal = false;
      editingConfig.value = null;
      newConfig.value = {
        reference_id: null,
        condition_id: null,
        condition_value: 'true',
      };
    };

    const onConditionerChange = (): void => {
      const selectedSection = sectionsStore.sections.find(s => s.id === newConfig.value.condition_id);
      if (!selectedSection) return;
      
      const availableConditions = availableConditionsForField.value;
      newConfig.value.condition_value = availableConditions[0]?.value || '';
    };

    const saveCondition = async (): Promise<void> => {
      if (!newConfig.value.reference_id || !newConfig.value.condition_id) {
        showNotification("Veuillez sélectionner un champ conditionneur.", "error");
        return;
      }

      const configData: FieldConfigCreate = {
        reference_id: newConfig.value.reference_id,
        condition_id: newConfig.value.condition_id,
        condition_value: newConfig.value.condition_value,
      };

      try {
        if (editingConfig.value) {
          await updateConfig(editingConfig.value.id, configData);
          showNotification("Condition mise à jour avec succès.", "success");
        } else {
          await createConfig(configData);
          showNotification("Condition ajoutée avec succès.", "success");
        }
        
        await sectionsStore.fetchConfigs();
        closeConditionModal();
      } catch (error) {
        showNotification(
          editingConfig.value 
            ? "Erreur lors de la mise à jour de la condition." 
            : "Erreur lors de l'ajout de la condition.",
          "error"
        );
      }
    };

    const deleteCondition = async (config: FieldConfigResponse): Promise<void> => {
      try {
        await deleteConfig(config.id);
        showNotification("Condition supprimée avec succès.", "success");
        await sectionsStore.fetchConfigs();
        closeConditionModal();
      } catch (error) {
        showNotification("Erreur lors de la suppression de la condition.", "error");
      }
    };

    // Actions pour le modal de section racine
    const openAddRootModal = (): void => {
      newRootSection.value = {
        name: '',
        description: '',
        display: true,
        mandatory_entry: false,
      };
      sectionsStore.showAddRootModal = true;
    };

    const closeAddRootModal = (): void => {
      sectionsStore.showAddRootModal = false;
      newRootSection.value = {
        name: '',
        description: '',
        display: true,
        mandatory_entry: false,
      };
    };

    const createRootSection = async (): Promise<void> => {
      if (!newRootSection.value.name.trim()) {
        showNotification("Le nom de la section est obligatoire.", "error");
        return;
      }
      
      if (!SectionUtils.isValidSectionName(newRootSection.value.name.trim())) {
        showNotification("Nom de section invalide. Utilisez uniquement des lettres, chiffres et underscores.", "error");
        return;
      }
      
      if (sectionsStore.sections.some(s => s.name === newRootSection.value.name.trim())) {
        showNotification("Une section avec ce nom existe déjà.", "error");
        return;
      }
      
      try {
        const sectionToCreate = {
          name: newRootSection.value.name.trim(),
          type: '',
          display: newRootSection.value.display,
          ordre: getNextRootOrder(),
          importance: 0,
          mandatory_entry: newRootSection.value.mandatory_entry
        };
        
        const result = await sectionsStore.createSection(sectionToCreate);
        if (result.success) {
          showNotification("Section racine ajoutée avec succès.", "success");
        } else {
          showNotification("Erreur lors de l'ajout de la section racine.", "error");
        }
      } finally {
        closeAddRootModal();
      }
    };

    // ✅ Les méthodes sont automatiquement exposées via le return

    return {
      sectionsStore,
      canCreate,
      canEdit,
      canDelete,
      sectionToDelete,
      currentSection,
      editingTranslation,
      editingConfig,
      newTranslation,
      newConfig,
      newRootSection,
      
      // Computed
      deleteModalMessage,
      availableLanguagesForSection,
      availableConditioners,
      availableConditionsForField,
      
      // Actions modal suppression
      openDeleteModal,
      closeDeleteModal,
      confirmDeleteSection,
      
      // Actions modal traduction
      openTranslationModal,
      closeTranslationModal,
      saveTranslation,
      deleteTranslation,
      
      // Actions modal condition
      openConditionModal,
      closeConditionModal,
      onConditionerChange,
      saveCondition,
      deleteCondition,
      
      // Actions modal section racine
      openAddRootModal,
      closeAddRootModal,
      createRootSection,
      
      // Utilitaires
      formatConditionerName,
    };
  }
});
</script>

<style scoped>
/* ===================================
   STYLES POUR LES MODAUX
   ================================== */

/* Overlay du modal */
.section-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  padding: 1rem;
}

/* Container du modal */
.section-modal-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  width: 100%;
}

/* Contenu du modal */
.section-modal-content {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.section-modal-content-sm {
  max-width: 28rem;
}

.section-modal-content-md {
  max-width: 32rem;
}

.section-modal-content-lg {
  max-width: 48rem;
}

.section-modal-content-xl {
  max-width: 64rem;
}

/* En-tête du modal */
.section-modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.section-modal-title {
  font-size: 1.125rem;
  line-height: 1.75rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

/* Corps du modal */
.section-modal-body {
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
}

/* Pied du modal */
.section-modal-footer {
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
}

/* Boutons du modal */
.section-modal-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
  min-width: 2.5rem;
  height: 2.5rem;
}

.section-modal-btn-primary {
  background-color: #2563eb;
  color: white;
}

.section-modal-btn-primary:hover {
  background-color: #1d4ed8;
}

.section-modal-btn-danger {
  background-color: #dc2626;
  color: white;
}

.section-modal-btn-danger:hover {
  background-color: #b91c1c;
}

.section-modal-btn-cancel {
  background-color: #6b7280;
  color: white;
}

.section-modal-btn-cancel:hover {
  background-color: #4b5563;
}

/* Formulaires */
.section-form-group {
  margin-bottom: 1rem;
}

.section-form-label {
  display: block;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
}

.section-form-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.section-form-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.section-form-select {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  background-color: white;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.section-form-select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.section-form-textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  resize: vertical;
  min-height: 4rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.section-form-textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.section-form-checkbox {
  width: 1rem;
  height: 1rem;
  color: #2563eb;
  border-radius: 0.25rem;
  border: 1px solid #d1d5db;
  margin-right: 0.5rem;
}

.section-form-checkbox:focus {
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.section-form-checkbox-label {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #374151;
  cursor: pointer;
}

/* Animation d'entrée */
@keyframes modal-fade-in {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.section-modal-content {
  animation: modal-fade-in 0.2s ease-out;
}

/* Responsive */
@media (max-width: 640px) {
  .section-modal-overlay {
    padding: 0.5rem;
  }
  
  .section-modal-content-sm,
  .section-modal-content-md,
  .section-modal-content-lg,
  .section-modal-content-xl {
    max-width: 100%;
    margin: 0;
  }
  
  .section-modal-header,
  .section-modal-body {
    padding: 1rem;
  }
  
  .section-modal-footer {
    padding: 0.75rem 1rem;
    flex-direction: column-reverse;
  }
  
  .section-modal-btn {
    width: 100%;
    justify-content: center;
  }
}

/* Accessibilité */
@media (prefers-reduced-motion: reduce) {
  .section-modal-content {
    animation: none;
  }
}
</style> 